import { add } from "date-fns"

type IExpiry = { days?: number; hours?: number; minutes?: number; seconds?: number }

async function cacheGetRecord(key: string) {
  return await window.skeem.fetch2<{ id: string; value: any; expires_at: Date } | null>({
    first: {
      name: "cache",
      matches: { key },
      attributes: ["value", "expires_at"],
    },
  })
}

async function cacheFetch<T>(key: string, expiry: IExpiry, fn: () => Promise<T>) {
  const cacheRecord = await cacheGetRecord(key)

  const isCacheValid =
    cacheRecord &&
    new Date(cacheRecord.expires_at) > new Date() &&
    process.env.NODE_ENV !== "development"

  if (isCacheValid) {
    return cacheRecord.value
  }

  if (cacheRecord) {
    console.warn(`[dbCache] Cache hit for key: ${key} but is dead`)
  }

  const value = await fn()
  const mutationData = {
    key,
    value,
    expires_at: parseExpiry(expiry),
  }
  try {
    if (cacheRecord) {
      await window.skeem.mutate({ cache: { update: { id: cacheRecord.id, ...mutationData } } })
    } else {
      await window.skeem.mutate({ cache: { create: mutationData } })
    }
  } catch (e) {
    console.error("[dbCache] Failed to write to cache", e)
  }
  return value
}

// Skeem helper fns
export async function skeemFetch2CacheFetch<T>(key: string, expiry: IExpiry, query: any) {
  return cacheFetch(key, expiry, async () => window.skeem.fetch2<T>(query))
}

export async function clearCacheKey(key: string) {
  const cacheRecord = await cacheGetRecord(key)
  if (cacheRecord) {
    await window.skeem.mutate({ cache: { destroy: cacheRecord.id } })
  }
}

function parseExpiry(expiry: IExpiry) {
  return add(new Date(), expiry)
}

export function constructCacheKey(parts: (string | number | boolean | object | undefined)[]) {
  return parts
    .map(part => {
      if (part === undefined) return "undefined"
      if (part === null) return "null"
      if (typeof part === "object") return JSON.stringify(part)
      return String(part)
    })
    .join("-")
}
